import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { TaskListItemModel, TemplatePageModel, TemplatePageNewPermissions } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/templatePage/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/templatePage/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/templatePage/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/templatePage/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/templatePage/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/templatePage/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/templatePage/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/templatePage/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/templatePage/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/templatePage/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/templatePage/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/templatePage/DELETE_FAILURE'

export type TemplatePageModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: TemplatePageModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedIds: [],
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadTemplatePages(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState): Promise<ActionResult<TemplatePageModel>> => {
    const loadedForKeys = [...getState().templatePages.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/template_pages.json', options)
      .then(({ data }: { data: TemplatePageModel[] }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadTemplatePage(templatePageId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.templatePages.loadedIds]

    if (templatePageId && !loadedIds.includes(templatePageId)){
      loadedIds.push(templatePageId)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/template_pages/${templatePageId}.json`, options)
      .then(({ data }: { data: TemplatePageModel }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createTemplatePage(templatePage: Partial<TemplatePageModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      template_page: templatePage,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/template_pages.json', options, config)
      .then(({ data }: { data: TemplatePageModel }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createTemplatePageFromTask(
  templatePage: Partial<TemplatePageModel>,
  taskListItem: Partial<TaskListItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      task_list_item_id: taskListItem.id,
      template_page: templatePage,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/template_pages/create_from_task.json', options, config)
      .then(({ data }: { data: TemplatePageModel }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateTemplatePage(
  templatePage: UpdateParams<TemplatePageModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      template_page: templatePage,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/template_pages/${templatePage.id}.json`, options, config)
      .then(({ data }: { data: TemplatePageModel }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function bulkUpdateTemplatePagePermissions(
  templatePage: UpdateParams<TemplatePageModel>,
  permissions: TemplatePageNewPermissions,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      permissions,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/template_pages/${templatePage.id}/bulk_update_permissions.json`, options, config)
      .then(({ data }: { data: TemplatePageModel }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function duplicateTemplatePage(templatePage: Partial<TemplatePageModel>, options: EntityOptions = {}) {
  const { id } = templatePage

  const config = {
    method: 'POST',
    data: JSON.stringify({
      template_page: templatePage,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/template_pages/${id}/duplicate.json`, options, config)
      .then(({ data }: { data: TemplatePageModel }) => {
        // Add New Template
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE)
        dispatch(updateEntities(normalizedJson))

        // Update Existing
        if (options.new_version){
          const existingPage = {
            ...templatePage,
            hidden: true,
          }

          const existingNormalized = normalize(existingPage, Schemas.TEMPLATE_PAGE)
          dispatch(updateEntities(existingNormalized))
        }

        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteTemplatePage(templatePage: DeleteParams<TemplatePageModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/template_pages/${templatePage.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function toggleProductionReady(templatePage: UpdateParams<TemplatePageModel>) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/template_pages/${templatePage.id}/toggle_production_ready.json`, {}, config)
      .then(({ data }: { data: TemplatePageModel }) => {
        const normalizedJson = normalize(data, Schemas.TEMPLATE_PAGE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: TemplatePageModuleState = initialState,
  action: Action = { type: '' },
): TemplatePageModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
